import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Skull, Ghost } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ArticleCard from "@/components/ArticleCard";
import Seo, { organizationSchema, websiteSchema } from "@/components/Seo";

const CAT_GRADIENT = {
  halloween: "linear-gradient(135deg, #FF6B00 0%, #4D00FF 100%)",
  couples: "linear-gradient(135deg, #FF3366 0%, #FF6B00 100%)",
  kids: "linear-gradient(135deg, #FFD814 0%, #FF3366 100%)",
  group: "linear-gradient(135deg, #4D00FF 0%, #FF3366 100%)",
  "pop-culture": "linear-gradient(135deg, #FF3366 0%, #4D00FF 100%)",
  decades: "linear-gradient(135deg, #FFD814 0%, #FF6B00 100%)",
  "party-supplies": "linear-gradient(135deg, #FF6B00 0%, #FFD814 100%)",
  accessories: "linear-gradient(135deg, #4D00FF 0%, #FF6B00 100%)",
  pets: "linear-gradient(135deg, #FFD814 0%, #4D00FF 100%)",
};

function catGradient(slug) {
  return CAT_GRADIENT[slug] || "linear-gradient(135deg, #FF3366 0%, #4D00FF 100%)";
}

function HeroCategoryTile({ cat }) {
  const [errored, setErrored] = useState(false);
  const showFallback = !cat.image_url || errored;
  if (showFallback) {
    return (
      <div
        className="brut-lg aspect-[4/5] relative grain flex items-center justify-center"
        style={{ background: catGradient(cat.slug) }}
      >
        <div className="font-display text-white text-6xl md:text-8xl tracking-tighter leading-[0.85] text-center px-6 drop-shadow-[4px_4px_0_rgba(0,0,0,1)]">
          {cat.name}
        </div>
      </div>
    );
  }
  return (
    <div className="brut-lg bg-white aspect-[4/5] overflow-hidden relative grain">
      <img
        src={cat.image_url}
        alt={cat.name}
        className="w-full h-full object-cover"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

function BentoTile({ cat }) {
  const [errored, setErrored] = useState(false);
  const showFallback = !cat.image_url || errored;
  return (
    <div className="relative w-full h-full">
      {showFallback ? (
        <div
          className="absolute inset-0 grain"
          style={{ background: catGradient(cat.slug) }}
        />
      ) : (
        <img
          src={cat.image_url}
          alt={cat.name}
          loading="lazy"
          onError={() => setErrored(true)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
      <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end">
        <div className="brut bg-white px-3 py-1.5 inline-block self-start font-mono uppercase text-[10px] tracking-widest mb-2">
          Category
        </div>
        <div className="font-display text-2xl md:text-4xl text-white drop-shadow-[2px_2px_0_rgba(0,0,0,1)] tracking-tight leading-none">
          {cat.name}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    // Try featured first; fall back to most recent if no featured products exist yet.
    api
      .get("/products", { params: { featured: true, page_size: 8 } })
      .then((r) => {
        if (r.data.items.length > 0) {
          setFeatured(r.data.items);
        } else {
          return api
            .get("/products", { params: { page_size: 8 } })
            .then((r2) => setFeatured(r2.data.items));
        }
      });
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3)));
  }, []);

  // Pick a hero category + 4 bento categories
  const heroCat = categories[0];
  const bentoCats = categories.slice(1, 5);

  // Memoise schema object so we don't recreate it on every render and force
  // the Seo component (which uses dangerouslySetInnerHTML) to re-stringify.
  const homeSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [organizationSchema(), websiteSchema()],
    }),
    []
  );

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        schema={homeSchema}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16 md:pb-24 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 brut bg-white px-3 py-1.5 mb-6 font-mono uppercase text-[11px] tracking-widest">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
              Halloween 2026 · Year 18
            </div>
            <h1 className="font-display text-[clamp(3rem,9vw,7.5rem)] leading-[0.85] tracking-tighter">
              Costumes,
              <br />
              <span className="pcs-text-pink">curated.</span>
              <br />
              Parties,
              <br />
              <span className="pcs-text-blue">delivered.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-gray-800">
              Thousands of vetted costumes &amp; party supplies — pulled from
              Amazon and eBay, hand-organized by people who actually wear
              costumes. No carts, no checkouts. Just pick, click, done.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/category/halloween"
                data-testid="hero-cta-halloween"
                className="brut pcs-bg-pink text-white px-6 py-3 font-bold uppercase tracking-wide brut-press inline-flex items-center gap-2"
              >
                <Skull className="w-4 h-4" strokeWidth={3} />
                Shop Halloween
              </Link>
              <Link
                to="/articles"
                data-testid="hero-cta-articles"
                className="brut bg-white text-black px-6 py-3 font-bold uppercase tracking-wide brut-press inline-flex items-center gap-2"
              >
                Read the guides
                <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </Link>
            </div>
          </div>

          {heroCat && (
            <div className="lg:col-span-5 relative">
              <HeroCategoryTile cat={heroCat} />
              <div className="absolute -top-4 -left-4 brut pcs-bg-orange px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest rotate-[-6deg]">
                <Ghost className="w-3 h-3 inline mr-1" strokeWidth={3} />
                Top pick
              </div>
              <Link
                to={`/category/${heroCat.slug}`}
                className="absolute -bottom-4 -right-4 brut pcs-bg-blue text-white px-4 py-2 font-bold uppercase tracking-wide text-sm brut-press"
                data-testid="hero-category-cta"
              >
                {heroCat.name} →
              </Link>
            </div>
          )}
        </div>

        {/* Vendor strip */}
        <div className="border-t-2 border-black bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-4 font-mono uppercase text-[11px] tracking-widest">
            <span className="opacity-70">Powered by affiliate partners:</span>
            <div className="flex flex-wrap gap-3 items-center">
              <span className="brut bg-[#FF9900] text-black px-3 py-1">Amazon</span>
              <span className="brut bg-[#E53238] text-white px-3 py-1">eBay</span>
              <span className="opacity-60">+ more coming soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* BENTO CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8 md:mb-12">
          <div>
            <div className="font-mono uppercase text-xs tracking-widest pcs-text-pink mb-2">
              Browse by mood
            </div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              Pick a vibe.
            </h2>
          </div>
          <Link
            to="/search"
            className="hidden md:inline-flex font-mono uppercase text-xs tracking-widest brut bg-white px-3 py-2 brut-press"
            data-testid="all-categories-link"
          >
            See all →
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {bentoCats.map((cat, i) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              data-testid={`bento-${cat.slug}`}
              className={`group brut bg-white overflow-hidden brut-hover ${
                i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"
              }`}
            >
              <BentoTile cat={cat} />
            </Link>
          ))}
        </div>

        {/* Remaining categories — chip row */}
        <div className="mt-8 flex flex-wrap gap-3">
          {categories.slice(5).map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              data-testid={`chip-${cat.slug}`}
              className="brut bg-white px-4 py-2 font-bold uppercase tracking-wide text-sm brut-press"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-y-2 border-black pcs-bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between mb-8 md:mb-12">
            <div>
              <div className="font-mono uppercase text-xs tracking-widest pcs-text-blue mb-2">
                Editor picks
              </div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
                Currently obsessed.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* ARTICLES TEASER */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8 md:mb-12">
          <div>
            <div className="font-mono uppercase text-xs tracking-widest pcs-text-pink mb-2">
              The party blog
            </div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              Read up.
            </h2>
          </div>
          <Link
            to="/articles"
            className="hidden md:inline-flex font-mono uppercase text-xs tracking-widest brut bg-white px-3 py-2 brut-press"
            data-testid="all-articles-link"
          >
            All articles →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      </section>
    </div>
  );
}
